// frontend/src/components/demo/DemoExitIntent.js
// Exit-intent modal shown only in demo mode when a visitor looks like they're
// leaving the site (cursor exits the top of the window). Once per session,
// it offers a clear path back to the real OGESTYLE so demo visitors aren't
// left stranded after "trying out the demo".

import React, { useEffect, useRef, useState } from "react";
import { useDemoMode } from "../../contexts/DemoModeContext";
import "./DemoExitIntent.css";

const PROD_URL = "https://www.ogestyle.com";

// Self-hosted lifestyle image used in the exit-intent modal (exists on demo + main).
const MODAL_IMG = "/landing/salon.jpg";

export default function DemoExitIntent() {
  const { isDemoMode } = useDemoMode();
  const [open, setOpen] = useState(false);
  const armedRef = useRef(false);
  const timerRef = useRef(null);

  // Only nudge guests — demo staff/owner accounts (logged in) are not nagged.
  const isLoggedIn =
    typeof window !== "undefined" &&
    Boolean(window.localStorage.getItem("ogestyle_token"));

  const showOnce = () => {
    if (sessionStorage.getItem("demoExitIntentShown") === "true") return;
    sessionStorage.setItem("demoExitIntentShown", "true");
    setOpen(true);
  };

  useEffect(() => {
    if (!isDemoMode || isLoggedIn) return undefined;
    if (sessionStorage.getItem("demoExitIntentShown") === "true") {
      return undefined;
    }

    const arm = () => {
      if (timerRef.current) return;
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        if (armedRef.current) showOnce();
      }, 450);
    };

    const disarm = () => {
      armedRef.current = false;
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const onMouseOut = (e) => {
      // Cursor left the viewport through the top edge with no target element
      // (heading for the address bar, another tab, or closing the window).
      if (!e.relatedTarget && e.clientY <= 0) {
        armedRef.current = true;
        arm();
      }
    };

    const onMouseOver = () => disarm();
    const onMouseMove = (e) => {
      if (e.clientY > 0) disarm();
    };

    // Mobile fallback: exit-intent (mouse) doesn't exist on touch devices, so
    // show the same nudge when a touch visitor returns to the tab after
    // switching away (once per session).
    const hasTouch =
      typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
    const onVisibility = () => {
      if (hasTouch && document.visibilityState === "visible") showOnce();
    };

    document.addEventListener("mouseout", onMouseOut);
    document.addEventListener("mouseover", onMouseOver);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      document.removeEventListener("mouseover", onMouseOver);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("visibilitychange", onVisibility);
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [isDemoMode, isLoggedIn]);

  if (!isDemoMode || isLoggedIn || !open) return null;

  const close = () => setOpen(false);

  return (
    <div className="exit-intent" role="dialog" aria-modal="true" aria-label="Leaving the demo">
      <button
        className="exit-intent__backdrop"
        onClick={close}
        aria-label="Close dialog"
        type="button"
      />
      <div className="exit-intent__card">
        <button
          className="exit-intent__close"
          onClick={close}
          aria-label="Dismiss"
          type="button"
        >
          ×
        </button>
        <img className="exit-intent__img" src={MODAL_IMG} alt="" />
        <div className="exit-intent__content">
          <p className="exit-intent__eyebrow">Before you go</p>
          <h2 className="exit-intent__title">From demo to real</h2>
          <p className="exit-intent__body">
            Create your free account to book, shop &amp; grow on the real
            OGESTYLE.
          </p>
          <div className="exit-intent__actions">
            <a
              className="exit-intent__btn exit-intent__btn--primary"
              href={PROD_URL}
              rel="noreferrer noopener"
            >
              Create your real account <span aria-hidden="true">&rarr;</span>
            </a>
            <button
              type="button"
              className="exit-intent__btn exit-intent__btn--ghost"
              onClick={close}
            >
              Stay in the demo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
